import { useAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import useAtomWithRef from '../../../../utils/useAtomWithRef'
import {
  skipBackwardsSecondsAtom,
  skipForwardsSecondsAtom,
  spacebarListenerActiveAtom,
  videoElementAtom,
} from '../atoms'
import { useRouter } from 'next/router'
import { getPageName } from '../../../../utils/amplitude_event_names'

/*======================== React component ===================================*/

const KeyHandler = (): JSX.Element => {
  const [videoElement] = useAtom(videoElementAtom)

  const [spaceBarListenerActiveRef] = useAtomWithRef(spacebarListenerActiveAtom)

  const [, setBackSeconds] = useAtom(skipBackwardsSecondsAtom)
  const [, setForwardSeconds] = useAtom(skipForwardsSecondsAtom)

  const router = useRouter()

  const backSeconds = useRef(0)
  const forwardSeconds = useRef(0)

  useEffect(() => {
    if (!videoElement) {
      return
    }

    let isHoldingKey = false
    let nextPressingDownAvailable = 0

    // For controlling the player with the keys, we use a time lock.
    // This creates a little time gap between the first input and the next ones, when the user
    // is holding a key.
    // When the user holds the key, we only allow the next action to be performed after the delay (of 200ms).
    // As we only need to schedule this delay once and not after every action (e.g. skipping 5 sec), we use
    // the variable 'isHoldingKey'.
    document.addEventListener('keydown', (e) => {
      if (getPageName(router.pathname) !== 'video') return

      if (Date.now() >= nextPressingDownAvailable) {
        if (e.code == 'ArrowLeft') {
          videoElement.currentTime -= 5
          backSeconds.current += 5
          setBackSeconds(backSeconds.current)
        } else if (e.code == 'ArrowRight') {
          videoElement.currentTime += 5
          forwardSeconds.current += 5
          setForwardSeconds(forwardSeconds.current)
        } else if (e.code == 'Space') {
          if (spaceBarListenerActiveRef.current) {
            e.preventDefault()
            videoElement.paused ? videoElement.play() : videoElement.pause()
          }
        }
      }

      if (!isHoldingKey) {
        isHoldingKey = true
        nextPressingDownAvailable = Date.now() + 200
      }
    })

    document.addEventListener('keyup', () => {
      // Reset the time lock.
      isHoldingKey = false
      // We need to reset this as well in order to allow for multiple pressing of a key without
      // holding it.
      nextPressingDownAvailable = 0

      backSeconds.current = 0
      forwardSeconds.current = 0
      setBackSeconds(0)
      setForwardSeconds(0)
    })
  }, [videoElement])

  return <></>
}

export default KeyHandler

/*======================== Styled components =================================*/
